import React, { useEffect, useState, useCallback } from 'react';
import { Tags, Search, Wrench, Plus, Edit2, Trash2, X } from 'lucide-react';
import { getKategoriSparepart, getInventoriSpareparts, renameKategori, deleteKategori, createKategori } from '../../api';

export default function KategoriSparepart() {
  const [kategoriList, setKategoriList] = useState([]);
  const [totalSparepart, setTotalSparepart] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedKategori, setSelectedKategori] = useState(null);
  const [filteredItems, setFilteredItems] = useState([]);
  const [search, setSearch] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [kategoriName, setKategoriName] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadKategori = useCallback(() => {
    setLoading(true);
    getKategoriSparepart()
      .then(res => {
        const data = res.data;
        setKategoriList(data.kategori || []);
        setTotalSparepart(data.total_sparepart || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadKategori();
  }, [loadKategori]);

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadKategori();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadKategori]);

  const handleSelectKategori = async (nama) => {
    setSelectedKategori(nama);
    setSearch('');
    setLoading(true);
    try {
      const res = await getInventoriSpareparts({ kategori: nama });
      setFilteredItems(res.data || []);
    } catch (err) {
      console.error(err);
      setFilteredItems([]);
    }
    setLoading(false);
  };

  // Open modal for adding
  const openAddModal = () => {
    setModalMode('add');
    setEditTarget(null);
    setKategoriName('');
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (kat, e) => {
    e.stopPropagation();
    setModalMode('edit');
    setEditTarget(kat);
    setKategoriName(kat.kategori);
    setShowModal(true);
  };

  // Save kategori
  const handleSaveKategori = async () => {
    const name = kategoriName.trim();
    if (!name) return;
    setSaving(true);
    try {
      if (modalMode === 'edit' && editTarget) {
        await renameKategori(editTarget.kategori, name);
      } else {
        await createKategori(name);
      }
      setShowModal(false);
      loadKategori();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || err.message;
      if (msg.includes('sudah ada')) {
        alert('Kategori "' + name + '" sudah ada.');
      } else {
        alert('Gagal menyimpan kategori: ' + msg);
      }
    }
    setSaving(false);
  };

  // Delete flow
  const handleDeleteKategori = (kat, e) => {
    e.stopPropagation();
    setDeleteTarget(kat);
  };

  const confirmDeleteKategori = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await deleteKategori(deleteTarget.kategori);
      setDeleteTarget(null);
      loadKategori();
    } catch (err) {
      console.error(err);
      alert('Gagal menghapus kategori: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  // Hover helpers for action buttons
  const showActions = (e) => {
    const card = e.currentTarget.closest('.kategori-card');
    if (card) {
      const el = card.querySelector('.kategori-actions');
      if (el) el.style.opacity = '1';
    }
  };
  const hideActions = (e) => {
    const card = e.currentTarget.closest('.kategori-card');
    if (card) {
      const el = card.querySelector('.kategori-actions');
      if (el) el.style.opacity = '0';
    }
  };

  if (loading && kategoriList.length === 0 && !selectedKategori) {
    return <div className="page-loading">Memuat data kategori...</div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Kategori Sparepart</h2>
            <p className="page-desc">{kategoriList.length} kategori, {totalSparepart} sparepart</p>
          </div>
          <button className="btn btn-primary" onClick={openAddModal} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> Tambah Kategori
          </button>
        </div>
      </div>

      {!selectedKategori ? (
        /* === KATEGORI GRID === */
        <div className="parts-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {kategoriList.map((kat, idx) => (
            <div
              key={idx}
              className="chart-card kategori-card"
              onClick={() => handleSelectKategori(kat.kategori)}
              style={{ cursor: 'pointer', transition: 'all 0.2s', position: 'relative' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; showActions(e); }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; hideActions(e); }}
            >
              {/* Hover action buttons */}
              <div
                className="kategori-actions"
                style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4, opacity: 0, transition: 'opacity 0.15s' }}
              >
                <button
                  className="btn-icon" title="Ubah nama kategori"
                  onClick={(e) => openEditModal(kat, e)}
                  style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'rgba(17,205,239,0.12)', color: '#11cdef', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Edit2 size={13} />
                </button>
                <button
                  className="btn-icon" title="Hapus kategori"
                  onClick={(e) => handleDeleteKategori(kat, e)}
                  style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'rgba(245,54,92,0.12)', color: '#f5365c', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <div style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: 'linear-gradient(135deg, #11cdef, #0d7a8a)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', flexShrink: 0
                }}>
                  <Tags size={20} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--gray-600)', marginBottom: 2 }}>{kat.kategori}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                    {kat.jumlah_item} item &middot; Stok: {kat.total_stok}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {kategoriList.length === 0 && (
            <div className="page-loading" style={{ gridColumn: '1 / -1' }}>
              <Tags size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
              <p>Belum ada kategori sparepart</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--gray-400)' }}>
                Klik <strong>"Tambah Kategori"</strong> untuk membuat kategori baru
              </p>
            </div>
          )}
        </div>
      ) : (
        /* === ITEMS FILTERED BY KATEGORI === */
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => { setSelectedKategori(null); setSearch(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Wrench size={14} /> Kembali ke kategori
            </button>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="search-box" style={{ flex: 1, minWidth: 200 }}>
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Cari sparepart..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)', fontWeight: 500 }}>
              <Tags size={14} style={{ marginRight: 4 }} />
              {selectedKategori} &middot; {filteredItems.length} item
            </div>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kode</th><th>Nama</th><th>Stok</th><th>Unit</th><th>Harga Beli</th><th>Rak</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.filter(sp =>
                  !search || sp.name?.toLowerCase().includes(search.toLowerCase()) ||
                  sp.code?.toLowerCase().includes(search.toLowerCase())
                ).map(sp => (
                  <tr key={sp.id}>
                    <td><code style={{ fontSize: '0.75rem' }}>{sp.code}</code></td>
                    <td style={{ fontWeight: 500 }}>{sp.name}</td>
                    <td style={{ fontWeight: 600, color: (sp.stock || 0) <= (sp.min_stok || 0) ? '#f5365c' : undefined }}>
                      {sp.stock}
                    </td>
                    <td>{sp.unit || '-'}</td>
                    <td style={{ fontWeight: 600 }}>Rp {(Number(sp.harga_beli || sp.price || 0)).toLocaleString('id-ID')}</td>
                    <td>{sp.rak || '-'}</td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada sparepart</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== MODAL ADD / EDIT ===== */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'add' ? 'Tambah Kategori' : 'Edit Kategori'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nama Kategori</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Masukkan nama kategori..."
                  value={kategoriName}
                  onChange={e => setKategoriName(e.target.value)}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveKategori(); }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSaveKategori} disabled={!kategoriName.trim() || saving}>
                {saving ? 'Menyimpan...' : (modalMode === 'add' ? 'Tambah' : 'Simpan')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE CONFIRM ===== */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal-content" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: '#f5365c' }}>Hapus Kategori</h3>
              <button className="btn-icon" onClick={() => setDeleteTarget(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p>
                Yakin ingin menghapus kategori <strong>"{deleteTarget.kategori}"</strong>?
              </p>
              <p style={{ fontSize: '0.82rem', color: 'var(--gray-400)', marginTop: 8 }}>
                Sparepart dalam kategori ini akan dipindahkan ke <em>Tidak Berkategori</em>.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Batal</button>
              <button className="btn" style={{ background: '#f5365c', color: 'white' }} onClick={confirmDeleteKategori}>
                Ya, Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .kategori-card:hover .kategori-actions {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
}
